import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Typography,
  Card,
  Tag,
  Badge,
  message,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  PictureOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { ImageStyle } from '../../types';
import { imageStylesApi } from '../../services/api/imageStyles';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate } from '../../utils';
import { PAGE_SIZE } from '../../constants';
import EditImageStyleModal from './EditImageStyleModal';

const { Title } = Typography;

const ImageStyles: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState<number>(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    return Number.isNaN(page) || page <= 0 ? 1 : page;
  });

  const [editingStyle, setEditingStyle] = useState<ImageStyle | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { execute: executeDelete } = useAsyncOperation();

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', String(currentPage));

    setSearchParams(params, { replace: true });
  }, [currentPage, setSearchParams]);

  const getImageStyles = useCallback(() => imageStylesApi.getImageStyles({
    pageIndex: currentPage - 1,
    pageSize: PAGE_SIZE,
  }), [currentPage]);

  const {
    data: stylesData,
    loading: stylesLoading,
    refresh: refreshStyles,
  } = useApi(getImageStyles, { immediate: true });

  useEffect(() => {
    refreshStyles();
  }, [refreshStyles, currentPage]);

  const handleAdd = () => {
    setEditingStyle(null);
    setIsModalVisible(true);
  };

  const handleEdit = (style: ImageStyle) => {
    setEditingStyle(style);
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    await executeDelete(
      () => imageStylesApi.deleteImageStyle(id),
      {
        successMessage: 'Style deleted successfully',
        errorMessage: 'Failed to delete style',
      }
    );
    refreshStyles();
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingStyle(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    refreshStyles();
  };

  const columns = [
    {
      title: 'Illustration',
      dataIndex: 'illustrationUrl',
      key: 'illustrationUrl',
      width: 120,
      render: (url: string, record: ImageStyle) => (
        url ? (
          <img 
            src={url} 
            alt={record.name} 
            style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4, border: '1px solid #f0f0f0' }} 
          />
        ) : (
          <div style={{ width: 80, height: 60, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}>
            <PictureOutlined style={{ color: '#ccc', fontSize: 24 }} />
          </div>
        )
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 180,
      render: (code: string) => (
        <Tag color="purple">
          {code}
        </Tag>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (name: string) => <Typography.Text strong>{name}</Typography.Text>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Badge
          status={status === 'ACTIVE' ? 'success' : 'default'}
          text={status}
        />
      ),
    },
    {
      title: 'Modified At',
      dataIndex: 'modifiedAt',
      key: 'modifiedAt',
      width: 180,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: ImageStyle) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure to delete this style?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>
            Image Style Library
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add New Style
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={stylesData?.data || []}
          rowKey="id"
          loading={stylesLoading}
          scroll={{ x: 1200 }}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: stylesData?.totalRecord || 0,
            onChange: (page) => setCurrentPage(page),
            showSizeChanger: false,
            showTotal: (total) => `Total ${total} styles`,
          }}
        />
      </Card>

      <EditImageStyleModal
        visible={isModalVisible}
        style={editingStyle}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default ImageStyles;
